import type { VoltrUpload } from '@/types/voltr';

/** A video the athlete picked (expo-image-picker asset, trimmed to what we use). */
export interface PickedVideo {
  uri: string;
  fileName: string;
  mimeType: string;
  /** From the picker when it knows; the size cap is checked before the file is read. */
  sizeBytes?: number | null;
  /** Web: the picked File itself, sliced directly so the video is never read into memory whole. */
  file?: Blob;
}

/**
 * `waiting` = every byte reached Voltr but it hadn't finished processing when
 * we stopped polling: not a failure, `checkWaiting` asks again (no re-upload).
 */
export type UploadPhase = 'idle' | 'preparing' | 'sending' | 'processing' | 'waiting' | 'done' | 'error';

/** One picked video and how far its upload has got. */
export interface UploadItem {
  key: string;
  video: PickedVideo;
  phase: UploadPhase;
  sentBytes: number;
  totalBytes: number;
  error: string | null;
  /** Set once every byte reached Voltr: the job `checkWaiting` (and a retry after a failed status check) re-polls. */
  jobId: string | null;
  /** The server's job for it, from the moment the job exists: the step's own card for that job gives way to this one. */
  serverJobId?: string;
  /** The file's checksum once hashed, so Try again doesn't hash a large file twice. */
  sha256?: string;
}

/**
 * What the picked videos are for, fixed when they're picked: the deal's
 * Action, the brand's video rights the athlete agreed to, and the sent-back
 * video they replace. Every upload uses these, never what a screen passes
 * later, so a video can't go to another deal or replace another video.
 */
export interface UploadContext {
  actionId: number;
  rightsVersion: string;
  replaces?: string;
}

/** Videos one deal can have waiting at once: they upload one after another, so keep the queue sane. */
export const MAX_PICKS = 10;

/**
 * The same video picked again: by name and size, since the picker hands
 * back a fresh copy (a new uri) each time.
 */
const sameVideo = (v: PickedVideo) => `${v.fileName}:${v.sizeBytes ?? v.uri}`;

/** `picks` added after `items`, skipping a video already there, up to MAX_PICKS. */
export function withPicks(items: UploadItem[], picks: PickedVideo[]): UploadItem[] {
  const have = new Set(items.map((i) => sameVideo(i.video)));
  const start = Date.now();
  const added = picks.filter((v) => !have.has(sameVideo(v)) && have.add(sameVideo(v))).map((v, i) => freshItem(v, start + i));
  return [...items, ...added].slice(0, MAX_PICKS);
}

/** `items` without the pick `key`, unless its bytes already reached Voltr (its job would still finish). */
export function withoutPick(items: UploadItem[], key: string): UploadItem[] {
  return items.some((i) => i.key === key && i.jobId) ? items : items.filter((i) => i.key !== key);
}

/** A pick before anything is sent. `n` keeps keys unique within the list. */
export function freshItem(video: PickedVideo, n: number): UploadItem {
  return { key: `${n}:${video.uri}`, video, phase: 'idle', sentBytes: 0, totalBytes: 0, error: null, jobId: null };
}

export function isTerminalFailure(upload: VoltrUpload): boolean {
  return ['failed', 'cancelled', 'canceled', 'expired'].includes(upload.status);
}

/** Logout (reset) happened while this upload was running: drop its result. */
export class Abandoned extends Error {}

/** How much of this video has gone up, 0–1. Past `sending`, every byte is with Voltr. */
export function sentFraction(item: UploadItem): number {
  if (item.phase === 'processing' || item.phase === 'waiting' || item.phase === 'done') return 1;
  return item.phase === 'sending' && item.totalBytes > 0 ? item.sentBytes / item.totalBytes : 0;
}

/** How far through the picked videos the upload is, 0–100: each video counts the same. */
export function percentSent(items: UploadItem[]): number {
  if (items.length === 0) return 0;
  const total = items.reduce((sum, item) => sum + sentFraction(item), 0);
  return Math.round((total / items.length) * 100);
}

/** Bytes still have to leave the phone, which only happens with the app on screen. Once they're all with Voltr, leaving costs nothing. A failed pick with a job already has its bytes there; one without gets sent again. */
export function bytesLeftToSend(items: UploadItem[]): boolean {
  return items.some((i) => i.phase === 'idle' || i.phase === 'preparing' || i.phase === 'sending' || (i.phase === 'error' && !i.jobId));
}

/** The "stay on this screen" band is up: a run is going with bytes left to send. */
export function bandShowing(running: boolean, items: UploadItem[]): boolean {
  return running && bytesLeftToSend(items);
}

/** A picked video's card already shows this server job, so the step doesn't show it twice. A finished pick has no card, so the server's stays; after a restart nothing is picked and the step's card is what's left. */
export function showsJob(items: UploadItem[], jobId: string): boolean {
  return items.some((i) => i.serverJobId === jobId && i.phase !== 'done');
}

/** Its bytes are on their way, or Voltr is taking them in: what a Cancel drops. */
export function inProgress(item: UploadItem): boolean {
  return item.phase === 'preparing' || item.phase === 'sending' || item.phase === 'processing';
}
